/**
 * Alta y edición de un proveedor de Compras.
 *
 * Portado del modal de `views/menu/proveedor/index.php` +
 * `Producto::{agregar,editar}_proveedor`: RUC (11 dígitos) con consulta a
 * **SUNAT** para traer la razón social, más nombre, descripción y teléfono.
 */

import { Search } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/http/api-error';

import { useActualizarProveedor, useConsultaRucProveedor, useCrearProveedor } from '../compras.api';
import type { DatosProveedor, ProveedorDetalle } from '../compras.types';

interface Props {
  /** Proveedor a editar; `null` = alta. */
  proveedor: ProveedorDetalle | null;
  abierto: boolean;
  onClose: () => void;
  /** Mensaje de éxito que muestra el listado. */
  onAviso: (mensaje: string) => void;
}

/** Formulario a partir del proveedor (o vacío). */
function formularioDe(proveedor: ProveedorDetalle | null): DatosProveedor {
  return {
    ruc: proveedor?.ruc ?? '',
    nombre: proveedor?.proveedor ?? '',
    descripcion: proveedor?.descripcion ?? '',
    telefono: proveedor?.telefono ?? '',
  };
}

export function ProveedorDialog({ proveedor, abierto, onClose, onAviso }: Props) {
  const crear = useCrearProveedor();
  const actualizar = useActualizarProveedor();
  const consulta = useConsultaRucProveedor();

  const [formulario, setFormulario] = useState<DatosProveedor>(() => formularioDe(proveedor));
  const [firma, setFirma] = useState(proveedor ? String(proveedor.id) : 'nuevo');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // El proveedor rellena el formulario en el render (guarda `firma`), sin efectos.
  const actual = proveedor ? String(proveedor.id) : 'nuevo';

  if (firma !== actual) {
    setFirma(actual);
    setFormulario(formularioDe(proveedor));
    setError(null);
    setAviso(null);
  }

  const cambiar = (campo: keyof DatosProveedor, valor: string) =>
    setFormulario((previo) => ({ ...previo, [campo]: valor }));

  const buscarRuc = () => {
    setError(null);
    setAviso(null);

    const numero = formulario.ruc.trim();

    if (numero === '') {
      setError('Escribe el RUC antes de buscarlo.');

      return;
    }

    consulta.mutate(numero, {
      onSuccess: (respuesta) => {
        const documento = respuesta.documento;

        setFormulario((previo) => ({ ...previo, nombre: documento.nombre ?? previo.nombre }));

        const detalle = [documento.estado, documento.condicion].filter(Boolean).join(' · ');

        setAviso(
          documento.nombre
            ? `SUNAT: ${documento.nombre}${detalle ? ` · ${detalle}` : ''}.`
            : 'SUNAT no devolvió la razón social; escríbela a mano.',
        );
      },
      onError: (fallo) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo consultar el RUC.'),
    });
  };

  const guardar = () => {
    setError(null);

    const datos: DatosProveedor = {
      ruc: formulario.ruc.trim(),
      nombre: formulario.nombre.trim(),
      descripcion: formulario.descripcion?.trim() || undefined,
      telefono: formulario.telefono?.trim() || undefined,
    };

    if (!datos.nombre) {
      setError('El nombre del proveedor es obligatorio.');

      return;
    }

    if (proveedor) {
      actualizar.mutate(
        { id: proveedor.id, datos },
        {
          onSuccess: (respuesta) => {
            onAviso(respuesta.mensaje);
            onClose();
          },
          onError: (fallo) =>
            setError(fallo instanceof ApiError ? fallo.message : 'No se pudo guardar el proveedor.'),
        },
      );

      return;
    }

    crear.mutate(datos, {
      onSuccess: (respuesta) => {
        onAviso(respuesta.mensaje);
        onClose();
      },
      onError: (fallo) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar el proveedor.'),
    });
  };

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      size="lg"
      title={proveedor ? `Editar proveedor · ${proveedor.proveedor ?? ''}` : 'Nuevo proveedor'}
      description="Se usa en las compras y en el pago a proveedores."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={crear.isPending || actualizar.isPending}
            onClick={guardar}
          >
            {proveedor ? 'Guardar cambios' : 'Registrar proveedor'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="No se pudo guardar">
            {error}
          </Alert>
        ) : null}

        {aviso ? (
          <Alert variant="info" title="Consulta a SUNAT">
            {aviso}
          </Alert>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Input
              label="RUC"
              hint="11 dígitos, sin guiones."
              inputMode="numeric"
              maxLength={11}
              value={formulario.ruc}
              onChange={(evento) =>
                cambiar('ruc', evento.target.value.replace(/\D/g, '').slice(0, 11))
              }
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              loading={consulta.isPending}
              leftIcon={<Search className="size-4" />}
              onClick={buscarRuc}
            >
              Buscar en SUNAT
            </Button>
          </div>

          <Input
            label="Razón social"
            value={formulario.nombre}
            onChange={(evento) => cambiar('nombre', evento.target.value)}
          />

          <Input
            label="Descripción"
            value={formulario.descripcion ?? ''}
            onChange={(evento) => cambiar('descripcion', evento.target.value)}
          />

          <Input
            label="Teléfono"
            inputMode="numeric"
            value={formulario.telefono ?? ''}
            onChange={(evento) =>
              cambiar('telefono', evento.target.value.replace(/\D/g, '').slice(0, 15))
            }
          />
        </div>
      </div>
    </Dialog>
  );
}
